import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import differenceBy from 'lodash/differenceBy';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { types as sdkTypes } from '../../../util/sdkLoader';
import { parse } from '../../../util/urlHelpers';
import { propTypes } from '../../../util/types';
import { ensureListing } from '../../../util/data';
import { sdkBoundsToFixedCoordinates, hasSameSDKBounds } from '../../../util/maps';
import {
  mapboxProjection as defaultMapboxProjection,
  mapboxStyle as defaultMapboxStyle,
} from '../../../config/configMaps';
import SearchMapIconPin from '../SearchMapIconPin/SearchMapIconPin';
import SearchMapPriceLabel from '../SearchMapPriceLabel/SearchMapPriceLabel';
import SearchMapInfoCard from '../SearchMapInfoCard/SearchMapInfoCard';
import SearchMapGroupLabel from '../SearchMapGroupLabel/SearchMapGroupLabel';
import {
  groupedByCoordinates,
  reducedToArray,
  getMobileInfoCardLayout,
  getMapReattachmentToken,
} from './SearchMap.helpers';
import css from './SearchMapWithMapbox.module.css';

import fuelPoints from './fuelPoints.json';
import icePoints from './icePoints.json';
import marinePoints from './marinePoints.json';
import pumpPoints from './pumpPoints.json';
import layerIcon from './Icons/layersIcon.png';
import { Icons } from '../../../components';
import CenterOnMyLocationButton from './CenterOnMyLocationButton';
import locationControlCss from './CenterOnMyLocationButton.module.css';

export const LABEL_HANDLE = 'SearchMapLabel';
export const INFO_CARD_HANDLE = 'SearchMapInfoCard';
export const SOURCE_AUTOCOMPLETE = 'autocomplete';
const BOUNDS_FIXED_PRECISION = 8;
const MOBILE_VIEWPORT_BREAKPOINT = 768;
const MOBILE_INFO_CARD_MARGIN = 12;
const MOBILE_INFO_CARD_GAP = 16;
const CURRENT_LOCATION_ZOOM = 14;

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

/**
 * Fit part of map (descriped with bounds) to visible map-viewport
 *
 * @param {Object} map - map that needs to be centered with given bounds
 * @param {SDK.LatLngBounds} bounds - the area that needs to be visible when map loads.
 */
export const fitMapToBounds = (map, bounds, options) => {
  const { padding = 0, isAutocompleteSearch = false } = options;

  // map bounds as string literal for google.maps
  const mapBounds = sdkBoundsToMapboxBounds(bounds);
  const paddingOptionMaybe = padding == null ? { padding } : {};
  const eventData = isAutocompleteSearch ? { searchSource: SOURCE_AUTOCOMPLETE } : {};

  // If bounds are given, use it (defaults to center & zoom).
  if (map && mapBounds) {
    map.fitBounds(mapBounds, { ...paddingOptionMaybe, linear: true, duration: 0 }, eventData);
  }
};

/**
 * Convert Mapbox formatted LatLng object to Sharetribe SDK's LatLng coordinate format
 * Longitudes > 180 and < -180 are converted to the correct corresponding value
 * between -180 and 180.
 *
 * @param {LngLat} mapboxLngLat - Mapbox LngLat
 *
 * @return {SDKLatLng} - Converted latLng coordinate
 */
export const mapboxLngLatToSDKLatLng = lngLat => {
  const mapboxLng = lngLat.lng;

  // For bounding boxes that overlap the antimeridian Mapbox sometimes gives
  // longitude values outside -180 and 180 degrees.Those values are converted
  // so that longitude is always between -180 and 180.
  const lng = mapboxLng > 180 ? mapboxLng - 360 : mapboxLng < -180 ? mapboxLng + 360 : mapboxLng;

  return new SDKLatLng(lngLat.lat, lng);
};

/**
 * Convert Mapbox formatted bounds object to Sharetribe SDK's bounds format
 *
 * @param {LngLatBounds} mapboxBounds - Mapbox LngLatBounds
 *
 * @return {SDKLatLngBounds} - Converted bounds
 */
export const mapboxBoundsToSDKBounds = mapboxBounds => {
  if (!mapboxBounds) {
    return null;
  }

  const ne = mapboxBounds.getNorthEast();
  const sw = mapboxBounds.getSouthWest();
  return new SDKLatLngBounds(mapboxLngLatToSDKLatLng(ne), mapboxLngLatToSDKLatLng(sw));
};

/**
 * Convert sdk bounds that overlap the antimeridian into values that can
 * be passed to Mapbox. This is achieved by converting the SW longitude into
 * a value less than -180 that flows over the antimeridian.
 *
 * @param {SDKLatLng} bounds - bounds passed to the map
 *
 * @return {LngLatBoundsLike} a bounding box that is compatible with Mapbox
 */
const sdkBoundsToMapboxBounds = bounds => {
  if (!bounds) {
    return null;
  }
  const { ne, sw } = bounds;

  // if sw lng is > ne lng => the bounds overlap antimeridian
  // => flip the nw lng to the negative side so that the value
  // is less than -180
  const swLng = sw.lng > ne.lng ? -360 + sw.lng : sw.lng;

  return [
    [swLng, sw.lat],
    [ne.lng, ne.lat],
  ];
};

/**
 * Return map bounds as SDKBounds
 *
 * @param {Mapbox} map - Mapbox map from where the bounds are asked
 *
 * @return {SDKLatLngBounds} - Converted bounds of given map
 */
export const getMapBounds = map => mapboxBoundsToSDKBounds(map.getBounds());

/**
 * Return map center as SDKLatLng
 *
 * @param {Mapbox} map - Mapbox map from where the center is asked
 *
 * @return {SDKLatLng} - Converted center of given map
 */
export const getMapCenter = map => mapboxLngLatToSDKLatLng(map.getCenter());

/**
 * Check if map library is loaded
 */
export const isMapsLibLoaded = () =>
  typeof window !== 'undefined' && window.mapboxgl && window.mapboxgl;

/**
 * Return price labels grouped by listing locations.
 * This is a helper function for SearchMapWithMapbox component.
 */
const priceLabelsInLocations = (
  listings,
  activeListingId,
  infoCardOpen,
  onListingClicked,
  mapComponentRefreshToken
) => {
  const listingArraysInLocations = reducedToArray(groupedByCoordinates(listings));
  const priceLabels = listingArraysInLocations.reverse().map(listingArr => {
    const isActive = activeListingId
      ? !!listingArr.find(l => activeListingId.uuid === l.id.uuid)
      : false;

    // If location contains only one listing, print price label
    if (listingArr.length === 1) {
      const listing = listingArr[0];
      const infoCardOpenIds = Array.isArray(infoCardOpen)
        ? infoCardOpen.map(l => l.id.uuid)
        : infoCardOpen
        ? [infoCardOpen.id.uuid]
        : [];

      // if the listing is open, don't print price label
      if (infoCardOpen != null && infoCardOpenIds.includes(listing.id.uuid)) {
        return null;
      }

      // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
      const { geolocation } = listing.attributes;

      const key = listing.id.uuid;
      return {
        markerId: `icon_${key}`, //`price_${key}`,
        location: geolocation,
        type: 'icon', //'price',
        componentProps: {
          key,
          isActive,
          className: LABEL_HANDLE,
          listing,
          onListingClicked,
          mapComponentRefreshToken,
        },
      };
    }

    // Explicit type change to object literal for Google OverlayViews (geolocation is SDK type)
    const firstListing = ensureListing(listingArr[0]);
    const geolocation = firstListing.attributes.geolocation;

    const key = listingArr[0].id.uuid;
    return {
      markerId: `group_${key}`,
      location: geolocation,
      type: 'group',
      componentProps: {
        key,
        isActive,
        className: LABEL_HANDLE,
        listings: listingArr,
        onListingClicked,
        mapComponentRefreshToken,
      },
    };
  });
  return priceLabels;
};

/**
 * Return info card. This is a helper function for SearchMapWithMapbox component.
 */
const infoCardComponent = (
  infoCardOpen,
  onListingInfoCardClicked,
  createURLToListing,
  mapComponentRefreshToken
) => {
  const listingsArray = Array.isArray(infoCardOpen) ? infoCardOpen : [infoCardOpen];

  if (!infoCardOpen) {
    return null;
  }

  const firstListing = ensureListing(listingsArray[0]);
  const key = firstListing.id.uuid;
  const geolocation = firstListing.attributes.geolocation;

  return {
    markerId: `infoCard_${key}`,
    location: geolocation,
    componentProps: {
      key,
      mapComponentRefreshToken,
      className: INFO_CARD_HANDLE,
      listings: listingsArray,
      onListingInfoCardClicked,
      createURLToListing,
    },
  };
};

function swapCoordinates(geojson) {
  // Iterate over the features array
  geojson.features.forEach(feature => {
    // Swap the coordinates
    const temp = feature.geometry.coordinates[0];
    feature.geometry.coordinates[0] = feature.geometry.coordinates[1];
    feature.geometry.coordinates[1] = temp;
  });
  return geojson;
}

/**
 * SearchMap component using Mapbox as map provider
 *
 * @component
 * @param {Object} props
 * @param {string} [props.id] - The ID
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {propTypes.latlngBounds} [props.bounds] - The bounds
 * @param {propTypes.latlng} [props.center] - The center
 * @param {Object} props.location - The location
 * @param {string} props.location.search - The search query params
 * @param {propTypes.uuid} [props.activeListingId] - The active listing ID
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {Function} props.onMapMoveEnd - The function to move end
 * @param {Function} props.onMapLoad - The function to load
 * @param {number} [props.zoom] - The zoom
 * @param {string} [props.reusableMapHiddenHandle] - The handle for the reusable map hidden
 * @param {Object} props.config - The configuration
 * @returns {JSX.Element}
 */
export class SearchMapWithMapboxComponent extends Component {
  constructor(props) {
    super(props);
    this.map = typeof window !== 'undefined' && window.mapboxMap ? window.mapboxMap : null;
    this.mobileInfoCardRef = React.createRef();
    this.mobileInfoCardAnimationFrame = null;
    this.mobileInfoCardResizeObserver = null;
    this.mobileInfoCardObservedElement = null;
    this.lastMobileInfoCardTransform = '';
    this.hasAttachedViewportListeners = false;
    this.userLocationMarker = null;

    this.state = {
      showLayers: false,
      mapContainer: null,
      isMapReady: false,
      currentMarkers: [],
      currentInfoCard: null,
    };
    this.viewportBounds = null;

    this.onMount = this.onMount.bind(this);
    this.onMoveend = this.onMoveend.bind(this);
    this.initializeMap = this.initializeMap.bind(this);
    this.handleDoubleClickOnInfoCard = this.handleDoubleClickOnInfoCard.bind(this);
    this.handleMobilePinchZoom = this.handleMobilePinchZoom.bind(this);
    this.handleLocationFound = this.handleLocationFound.bind(this);
    this.attachViewportListeners = this.attachViewportListeners.bind(this);
    this.isMobileViewport = this.isMobileViewport.bind(this);
    this.onToggleLayer = this.onToggleLayer.bind(this);
    this.resetMobileInfoCardPosition = this.resetMobileInfoCardPosition.bind(this);
    this.scheduleMobileInfoCardPosition = this.scheduleMobileInfoCardPosition.bind(this);
    this.syncMobileInfoCardObserver = this.syncMobileInfoCardObserver.bind(this);
    this.updateMarkers = this.updateMarkers.bind(this);
    this.updateMobileInfoCardPosition = this.updateMobileInfoCardPosition.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const prevReattachmentToken = getMapReattachmentToken(prevProps.mapComponentRefreshToken);
    const nextReattachmentToken = getMapReattachmentToken(this.props.mapComponentRefreshToken);

    if (!isEqual(prevProps.location, this.props.location)) {
      // If no mapSearch url parameter is given, this is original location search
      const { mapSearch } = parse(this.props.location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });
      if (!mapSearch) {
        this.viewportBounds = null;
      }
    }

    if (this.map) {
      const currentBounds = getMapBounds(this.map);

      // Do not call fitMapToBounds if bounds are the same.
      // Our bounds are viewport bounds, and fitBounds will try to add margins around those bounds
      // that would result to zoom-loop (bound change -> fitmap -> bounds change -> ...)
      if (!isEqual(this.props.bounds, currentBounds) && !this.viewportBounds) {
        fitMapToBounds(this.map, this.props.bounds, { padding: 0, isAutocompleteSearch: true });
      }
    }

    if (!this.map && this.state.mapContainer) {
      this.initializeMap();

      /* Notify parent component that Mapbox map is loaded */
      this.props.onMapLoad(this.map);
    } else if (prevReattachmentToken !== nextReattachmentToken) {
      /* Notify parent component that Mapbox map is loaded */
      this.map.resize();
      this.props.onMapLoad(this.map);
    }

    const mapBecameAvailable =
      !!this.map &&
      ((!!this.state.mapContainer && prevState.mapContainer !== this.state.mapContainer) ||
        (!prevState.isMapReady && this.state.isMapReady));

    // Update markers if listings, infoCardOpen, or mapComponentRefreshToken change.
    // activeListingId highlights are handled directly in render without re-creating marker instances.
    // Mobile opens the map after search results have often already loaded, so markers also need
    // to be initialized when the map first becomes available.
    if (
      mapBecameAvailable ||
      !isEqual(prevProps.listings, this.props.listings) ||
      !isEqual(prevProps.infoCardOpen, this.props.infoCardOpen) ||
      prevProps.mapComponentRefreshToken !== this.props.mapComponentRefreshToken
    ) {
      this.updateMarkers();
    }

    this.attachViewportListeners();

    const shouldPositionMobileInfoCard =
      this.map &&
      this.state.isMapReady &&
      this.state.currentInfoCard &&
      !this.state.currentInfoCard.markerContainer &&
      this.isMobileViewport();

    if (shouldPositionMobileInfoCard) {
      this.syncMobileInfoCardObserver();

      if (
        prevState.currentInfoCard !== this.state.currentInfoCard ||
        prevState.isMapReady !== this.state.isMapReady ||
        prevState.mapContainer !== this.state.mapContainer ||
        !isEqual(prevProps.infoCardOpen, this.props.infoCardOpen) ||
        prevProps.mapComponentRefreshToken !== this.props.mapComponentRefreshToken
      ) {
        this.scheduleMobileInfoCardPosition();
      }
    } else {
      this.resetMobileInfoCardPosition();
      this.syncMobileInfoCardObserver();
    }
  }

  componentWillUnmount() {
    if (this.map && this.hasAttachedViewportListeners) {
      this.map.off('move', this.scheduleMobileInfoCardPosition);
      this.map.off('resize', this.scheduleMobileInfoCardPosition);
      window.removeEventListener('resize', this.scheduleMobileInfoCardPosition);
      this.hasAttachedViewportListeners = false;
    }
    if (this.map) {
      this.map.off('moveend', this.onMoveend);
    }
    if (this.userLocationMarker) {
      this.userLocationMarker.remove();
      this.userLocationMarker = null;
    }
    if (this.state.currentInfoCard?.markerContainer) {
      this.state.currentInfoCard.marker?.remove();
      this.state.currentInfoCard.markerContainer.removeEventListener(
        'dblclick',
        this.handleDoubleClickOnInfoCard
      );
    }
    if (this.mobileInfoCardAnimationFrame) {
      window.cancelAnimationFrame(this.mobileInfoCardAnimationFrame);
      this.mobileInfoCardAnimationFrame = null;
    }
    if (this.mobileInfoCardResizeObserver && this.mobileInfoCardObservedElement) {
      this.mobileInfoCardResizeObserver.unobserve(this.mobileInfoCardObservedElement);
    }
    this.mobileInfoCardResizeObserver = null;
    this.mobileInfoCardObservedElement = null;
    document.removeEventListener('gesturestart', this.handleMobilePinchZoom, false);
    document.removeEventListener('gesturechange', this.handleMobilePinchZoom, false);
    document.removeEventListener('gestureend', this.handleMobilePinchZoom, false);
  }

  onMount(element) {
    // This prevents pinch zoom to affect whole page on mobile Safari.
    document.addEventListener('gesturestart', this.handleMobilePinchZoom, false);
    document.addEventListener('gesturechange', this.handleMobilePinchZoom, false);
    document.addEventListener('gestureend', this.handleMobilePinchZoom, false);

    this.setState({ mapContainer: element });
  }

  onMoveend(e) {
    if (this.map) {
      // If reusableMapHiddenHandle is given and parent element has that class,
      // we don't listen moveend events.
      // This fixes mobile Chrome bug that sends map events to invisible map components.
      const isHiddenByReusableMap =
        this.props.reusableMapHiddenHandle &&
        this.state.mapContainer?.parentElement?.classList?.contains(
          this.props.reusableMapHiddenHandle
        );
      if (!isHiddenByReusableMap) {
        const viewportMapBounds = getMapBounds(this.map);
        const viewportMapCenter = getMapCenter(this.map);
        const viewportMapZoom = this.map?.getZoom?.();
        const viewportBounds = sdkBoundsToFixedCoordinates(
          viewportMapBounds,
          BOUNDS_FIXED_PRECISION
        );

        // ViewportBounds from (previous) rendering differ from viewportBounds currently set to map
        // I.e. user has changed the map somehow: moved, panned, zoomed, resized
        const viewportBoundsChanged =
          this.viewportBounds && !hasSameSDKBounds(this.viewportBounds, viewportBounds);
        this.onToggleLayer(false);
        this.props.onMapMoveEnd(viewportBoundsChanged, {
          viewportBounds,
          viewportMapCenter,
          viewportMapZoom,
        });
        this.viewportBounds = viewportBounds;
      }
    }
  }

  getlinkContent = (id, iconsData) => {
    //icon
    const mapLinkIcon = document.createElement('img');
    mapLinkIcon.id = id;
    mapLinkIcon.src = iconsData[0].iconUrl;
    mapLinkIcon.alt = id;
    mapLinkIcon.className = `${css.mapLinkIcon}`;

    //label
    const mapLinkLabel = document.createElement('span');
    mapLinkLabel.id = id;
    mapLinkLabel.textContent = id;
    mapLinkLabel.className = `${css.mapLinkLabel}`;

    //create icon
    const mapLinkTitle = document.createElement('div');
    mapLinkTitle.id = id;
    mapLinkTitle.className = `${css.mapLinkTitle}`;
    mapLinkTitle.appendChild(mapLinkIcon);
    mapLinkTitle.appendChild(mapLinkLabel);

    //checkbox
    const mapLinkSwitchInput = document.createElement('input');
    mapLinkSwitchInput.id = id;
    mapLinkSwitchInput.type = 'checkbox';
    mapLinkSwitchInput.checked = false;
    mapLinkSwitchInput.className = `${css.mapLinkSwitchInput}`;

    mapLinkSwitchInput.onclick = function(e) {
      const clickedLayer = e.target.id;
      e.stopPropagation();

      const visibility = window.mapboxMap.getLayoutProperty(clickedLayer, 'visibility');

      if (visibility === 'visible') {
        window.mapboxMap.setLayoutProperty(clickedLayer, 'visibility', 'none');
        this.style.backgroundColor = 'white';
        this.style.color = 'black';
      } else {
        this.style.backgroundColor = '#047cc3';
        this.style.color = 'white';
        window.mapboxMap.setLayoutProperty(clickedLayer, 'visibility', 'visible');
      }
    };

    //slider
    const mapLinkSwitchSlider = document.createElement('span');
    mapLinkSwitchSlider.id = id;
    mapLinkSwitchSlider.className = `${css.mapLinkSwitchSlider}`;

    //toggle
    const mapLinkSwitch = document.createElement('toggle');
    mapLinkSwitch.id = id;
    mapLinkSwitch.className = `${css.mapLinkSwitch}`;
    mapLinkSwitch.appendChild(mapLinkSwitchInput);
    mapLinkSwitch.appendChild(mapLinkSwitchSlider);

    // Create a link.
    const mapLink = document.createElement('div');
    mapLink.id = id;
    mapLink.className = `${css.mapLink}`;
    mapLink.appendChild(mapLinkTitle);
    mapLink.appendChild(mapLinkSwitch);

    return mapLink;
  };

  onToggleLayer(boolean) {
    this.setState({ showLayers: boolean });
  }

  handleLocationFound(location) {
    if (!this.map) {
      return;
    }

    const coordinates = [location.lng, location.lat];

    if (this.userLocationMarker) {
      this.userLocationMarker.setLngLat(coordinates);
    } else {
      const markerElement = document.createElement('div');
      markerElement.className = locationControlCss.userLocationMarker;
      markerElement.setAttribute('aria-hidden', 'true');
      this.userLocationMarker = new window.mapboxgl.Marker({
        element: markerElement,
        anchor: 'center',
      })
        .setLngLat(coordinates)
        .addTo(this.map);
    }

    this.map.flyTo({
      center: coordinates,
      zoom: CURRENT_LOCATION_ZOOM,
      duration: 1200,
      essential: true,
    });
  }

  attachViewportListeners() {
    if (!this.map || !this.state.mapContainer || this.hasAttachedViewportListeners) {
      return;
    }

    this.map.on('move', this.scheduleMobileInfoCardPosition);
    this.map.on('resize', this.scheduleMobileInfoCardPosition);
    window.addEventListener('resize', this.scheduleMobileInfoCardPosition);
    this.hasAttachedViewportListeners = true;
  }

  isMobileViewport() {
    return typeof window !== 'undefined' && window.innerWidth <= MOBILE_VIEWPORT_BREAKPOINT;
  }

  syncMobileInfoCardObserver() {
    const overlayElement = this.mobileInfoCardRef.current;
    const canObserve =
      typeof window !== 'undefined' &&
      typeof window.ResizeObserver !== 'undefined' &&
      overlayElement;

    if (!canObserve) {
      if (this.mobileInfoCardResizeObserver && this.mobileInfoCardObservedElement) {
        this.mobileInfoCardResizeObserver.unobserve(this.mobileInfoCardObservedElement);
      }
      this.mobileInfoCardObservedElement = null;
      return;
    }

    if (!this.mobileInfoCardResizeObserver) {
      this.mobileInfoCardResizeObserver = new window.ResizeObserver(() => {
        this.scheduleMobileInfoCardPosition();
      });
    }

    if (this.mobileInfoCardObservedElement !== overlayElement) {
      if (this.mobileInfoCardObservedElement) {
        this.mobileInfoCardResizeObserver.unobserve(this.mobileInfoCardObservedElement);
      }

      this.mobileInfoCardResizeObserver.observe(overlayElement);
      this.mobileInfoCardObservedElement = overlayElement;
    }
  }

  scheduleMobileInfoCardPosition() {
    if (!this.isMobileViewport() || !this.state.currentInfoCard || !this.map) {
      return;
    }

    if (this.mobileInfoCardAnimationFrame) {
      window.cancelAnimationFrame(this.mobileInfoCardAnimationFrame);
    }

    this.mobileInfoCardAnimationFrame = window.requestAnimationFrame(() => {
      this.mobileInfoCardAnimationFrame = null;
      this.updateMobileInfoCardPosition();
    });
  }

  resetMobileInfoCardPosition() {
    const overlayElement = this.mobileInfoCardRef.current;

    if (overlayElement) {
      overlayElement.classList.remove(css.infoCardOverlayReady);
      overlayElement.style.transform = '';
      overlayElement.style.removeProperty('--mobile-info-card-transform');
    }

    this.lastMobileInfoCardTransform = '';
  }

  updateMobileInfoCardPosition() {
    const overlayElement = this.mobileInfoCardRef.current;
    const { currentInfoCard, mapContainer } = this.state;

    if (
      !this.map ||
      !mapContainer ||
      !overlayElement ||
      !currentInfoCard ||
      currentInfoCard.markerContainer ||
      !this.isMobileViewport()
    ) {
      return;
    }

    const { clientWidth: containerWidth, clientHeight: containerHeight } = mapContainer;
    const cardWidth = overlayElement.offsetWidth;
    const cardHeight = overlayElement.offsetHeight;
    const projectedPoint = this.map.project([
      currentInfoCard.location.lng,
      currentInfoCard.location.lat,
    ]);

    const layout = getMobileInfoCardLayout({
      containerWidth,
      containerHeight,
      cardWidth,
      cardHeight,
      pointX: projectedPoint.x,
      pointY: projectedPoint.y,
      gap: MOBILE_INFO_CARD_GAP,
      margin: MOBILE_INFO_CARD_MARGIN,
    });

    if (!layout) {
      return;
    }

    const nextTransform = `translate3d(${Math.round(layout.left)}px, ${Math.round(
      layout.top
    )}px, 0)`;
    if (this.lastMobileInfoCardTransform !== nextTransform) {
      overlayElement.style.transform = nextTransform;
      this.lastMobileInfoCardTransform = nextTransform;
    }

    overlayElement.classList.add(css.infoCardOverlayReady);
  }

  initializeMap() {
    const { offsetHeight, offsetWidth } = this.state.mapContainer;
    const mapboxStyle = this.props.config?.maps?.mapboxStyle || defaultMapboxStyle;
    const mapboxProjection = this.props.config?.maps?.mapboxProjection || defaultMapboxProjection;
    const hasDimensions = offsetHeight > 0 && offsetWidth > 0;
    if (hasDimensions) {
      this.map = new window.mapboxgl.Map({
        container: this.state.mapContainer,
        style: mapboxStyle,
        projection: mapboxProjection,
        scrollZoom: true,
        dragPan: true,
        dragRotate: true,
        touchZoomRotate: true,
        boxZoom: true,
      });
      window.mapboxMap = this.map;

      var nav = new window.mapboxgl.NavigationControl({ showCompass: false });
      this.map.addControl(nav, 'top-left');
      // Define icons and their corresponding data sources
      const iconsData = [
        {
          name: 'Fuel docks',
          iconUrl: 'https://onbuoy-main.s3.us-west-2.amazonaws.com/fuelIcon.png',
          dataSource: fuelPoints,
        },
        {
          name: 'Ice machines',
          iconUrl: 'https://onbuoy-main.s3.us-west-2.amazonaws.com/IceIcon.png',
          dataSource: swapCoordinates(icePoints),
        },
        {
          name: 'Certified Clean Marinas',
          iconUrl: 'https://onbuoy-main.s3.us-west-2.amazonaws.com/marinIcon.png',
          dataSource: swapCoordinates(marinePoints),
        },
        {
          name: 'Pump out stations',
          iconUrl: 'https://onbuoy-main.s3.us-west-2.amazonaws.com/pumpIcon.png',
          dataSource: swapCoordinates(pumpPoints),
        },
      ];
      this.map.on('load', () => {
        this.setState({ isMapReady: true });
        iconsData.forEach((item, index) => {
          this.map.loadImage(item.iconUrl, (error, image) => {
            if (error) throw error;

            // Add the image to the map style
            this.map.addImage(`icon-${index}`, image);
            this.map.addSource(`point-${index}`, {
              type: 'geojson',
              data: item.dataSource,
            });

            // Add a layer for each data source using the corresponding icon
            this.map.addLayer({
              id: item?.name,
              type: 'symbol',
              source: `point-${index}`,
              layout: {
                visibility: 'none', // Set default visibility to 'none'
                'icon-image': `icon-${index}`,
                'icon-size': 0.25,
              },
            });
          });
        });
      });

      // After the last frame rendered before the map enters an "idle" state.
      this.map.on('idle', () => {
        // Enumerate ids of the layers.
        const toggleableLayerIds = [
          'Fuel docks',
          'Ice machines',
          'Certified Clean Marinas',
          'Pump out stations',
        ];

        // Set up the corresponding toggle button for each layer.
        for (const id of toggleableLayerIds) {
          // Skip layers that already have a button set up.
          if (document.getElementById(id)) {
            continue;
          }

          // Show or hide layer when the toggle is clicked.

          const link = this.getlinkContent(
            id,
            iconsData.filter(icon => icon.name === id)
          );

          const layers = document.getElementById('menu');
          if (layers) {
            layers.appendChild(link);
          }
        }
      });
      this.map.on('moveend', this.onMoveend);
      this.map.on('click', e => {
        this.props.onClick({ nativeEvent: e.originalEvent });
      });
    }
  }

  updateMarkers() {
    const {
      listings = [],
      activeListingId,
      infoCardOpen,
      onListingClicked,
      mapComponentRefreshToken,
      onListingInfoCardClicked,
      createURLToListing,
    } = this.props;
    const isMobileViewport = this.isMobileViewport();

    if (this.map) {
      // Create markers out of price labels and grouped labels
      const labels = priceLabelsInLocations(
        listings,
        activeListingId,
        infoCardOpen,
        onListingClicked,
        mapComponentRefreshToken
      );

      // If map has moved or info card opened, unnecessary markers need to be removed
      const removableMarkers = differenceBy(this.state.currentMarkers, labels, 'markerId');
      removableMarkers.forEach(rm => rm.marker.remove());

      // Helper function to create markers to given container
      const createMarker = (data, markerContainer) =>
        new window.mapboxgl.Marker(markerContainer, { anchor: 'bottom' })
          .setLngLat([data.location.lng, data.location.lat])
          .addTo(this.map);
      const removeCurrentInfoCardMarker = () => {
        if (this.state.currentInfoCard?.markerContainer) {
          this.state.currentInfoCard.marker?.remove();
          this.state.currentInfoCard.markerContainer.removeEventListener(
            'dblclick',
            this.handleDoubleClickOnInfoCard
          );
        }
      };

      // SearchMapPriceLabel and SearchMapGroupLabel:
      // create a new marker or use existing one if markerId is among previously rendered markers
      const newMarkers = labels
        .filter(v => v != null)
        .map(m => {
          const existingMarkerId = this.state.currentMarkers.findIndex(
            marker => m.markerId === marker.markerId && marker.marker
          );

          let markerContainer;
          let marker;
          if (existingMarkerId >= 0) {
            ({ marker, markerContainer } = this.state.currentMarkers[existingMarkerId]);
          } else {
            markerContainer = document.createElement('div');
            markerContainer.setAttribute('id', m.markerId);
            markerContainer?.classList.add(css.labelContainer);
            markerContainer.addEventListener('click', e => e.stopPropagation());
            marker = createMarker(m, markerContainer);
          }
          return { ...m, markerContainer, marker };
        });

      /* Create marker for SearchMapInfoCard component */
      let newInfoCard = null;
      if (infoCardOpen) {
        removeCurrentInfoCardMarker();

        const infoCard = infoCardComponent(
          infoCardOpen,
          onListingInfoCardClicked,
          createURLToListing,
          mapComponentRefreshToken
        );

        if (isMobileViewport) {
          newInfoCard = { ...infoCard };
        } else {
          // marker container and its styles
          const infoCardContainer = document.createElement('div');
          infoCardContainer.setAttribute('id', infoCard.markerId);
          infoCardContainer.classList.add(css.infoCardContainer);
          infoCardContainer.addEventListener('dblclick', this.handleDoubleClickOnInfoCard, false);
          infoCardContainer.addEventListener('click', e => e.stopPropagation());

          newInfoCard = {
            ...infoCard,
            markerContainer: infoCardContainer,
            marker: infoCard ? createMarker(infoCard, infoCardContainer) : null,
          };
        }
      } else {
        removeCurrentInfoCardMarker();
      }

      this.setState({ currentMarkers: newMarkers, currentInfoCard: newInfoCard });
    }
  }

  handleMobilePinchZoom(e) {
    e.preventDefault();
    // A hack to prevent pinch zoom gesture in mobile Safari
    // Otherwise, pinch zoom would zoom both map and the document.
    document.body.style.zoom = 0.99;
  }

  handleDoubleClickOnInfoCard(e) {
    e.stopPropagation();
  }

  render() {
    const { id = 'searchMap', className, activeListingId, config, currentUser } = this.props;

    return (
      <>
        <div id={id} ref={this.onMount} className={classNames(className, css.fullArea)}>
          {this.state.currentMarkers.map(m => {
            // Remove existing activeLabel classes and add it only to the correct container
            m.markerContainer.classList.remove(css.activeLabel);
            if (activeListingId && activeListingId.uuid === m.componentProps.key) {
              m.markerContainer.classList.add(css.activeLabel);
            }

            const isMapReadyForMarkers = this.map && m.markerContainer && this.state.isMapReady;
            // DOM node that should be used as portal's root
            const portalDOMContainer = isMapReadyForMarkers
              ? document.getElementById(m.markerContainer.id)
              : null;

            const componentProps = omit(m.componentProps, ['key']);

            // Create component portals for correct marker containers
            if (isMapReadyForMarkers && m.type === 'price') {
              return ReactDOM.createPortal(
                <SearchMapPriceLabel {...componentProps} config={config} />,
                portalDOMContainer
              );
            } else if (isMapReadyForMarkers && m.type === 'icon') {
              return ReactDOM.createPortal(
                <SearchMapIconPin {...componentProps} />,
                portalDOMContainer
              );
            } else if (isMapReadyForMarkers && m.type === 'group') {
              return ReactDOM.createPortal(
                <SearchMapGroupLabel {...componentProps} />,
                portalDOMContainer
              );
            }
            return null;
          })}
          {this.state.mapContainer && this.state.currentInfoCard && this.state.isMapReady ? (
            this.state.currentInfoCard.markerContainer ? (
              ReactDOM.createPortal(
                <SearchMapInfoCard
                  {...omit(this.state.currentInfoCard.componentProps, ['key'])}
                  config={config}
                  currentUser={currentUser}
                />,
                this.state.currentInfoCard.markerContainer
              )
            ) : (
              <div ref={this.mobileInfoCardRef} className={css.infoCardOverlay}>
                <SearchMapInfoCard
                  {...omit(this.state.currentInfoCard.componentProps, ['key'])}
                  config={config}
                  currentUser={currentUser}
                />
              </div>
            )
          ) : null}
        </div>
        <div
          className={css.toggleLayer}
          role="button"
          onClick={() => {
            this.onToggleLayer(!this.state.showLayers);
          }}
        >
          <img src={layerIcon} alt="Toggle Icon" />
        </div>
        {this.state.isMapReady ? (
          <CenterOnMyLocationButton onLocationFound={this.handleLocationFound} />
        ) : null}
        <nav
          className={classNames(css.layersMenu, {
            [css.hideMenu]: !this.state.showLayers,
          })}
          id="menu"
        >
          <div className={css.mapOptions}>
            <div className={css.title}>
              <Icons name="stack" /> Map Options
            </div>
          </div>
        </nav>
      </>
    );
  }
}

SearchMapWithMapboxComponent.defaultProps = {
  id: 'map',
  center: null,
  priceLabels: [],
  infoCard: null,
  zoom: 11,
  reusableMapHiddenHandle: null,
};

export default SearchMapWithMapboxComponent;
